/**
 * Meeting Favorites Hook
 * Provides functionality for bookmarking/favoriting meetings
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

// Query key for meeting favorites
export const meetingFavoritesKey = (userId: string | undefined) => 
  ['meeting-favorites', userId] as const;

/**
 * Hook to get all favorited meeting IDs for current user
 * Returns a Set for O(1) lookup performance
 */
export const useFavoriteMeetingIds = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: meetingFavoritesKey(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_meeting_favorites')
        .select('meeting_id')
        .eq('user_id', user!.id);

      if (error) throw error;
      return new Set(data?.map(f => f.meeting_id) || []);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to toggle favorite status for a meeting
 */
export const useToggleMeetingFavorite = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ meetingId, isFavorite }: { meetingId: string; isFavorite: boolean }) => {
      if (!user?.id) throw new Error('User not authenticated');

      if (isFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('user_meeting_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('meeting_id', meetingId);

        if (error) throw error;
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('user_meeting_favorites')
          .insert({ user_id: user.id, meeting_id: meetingId });

        if (error) throw error;
      }
    },
    onMutate: async ({ meetingId, isFavorite }) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: meetingFavoritesKey(user?.id) });
      
      const previousFavorites = queryClient.getQueryData<Set<string>>(meetingFavoritesKey(user?.id));
      
      queryClient.setQueryData<Set<string>>(meetingFavoritesKey(user?.id), (old) => {
        const newSet = new Set(old || []);
        if (isFavorite) {
          newSet.delete(meetingId);
        } else {
          newSet.add(meetingId);
        }
        return newSet;
      });

      return { previousFavorites };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousFavorites) {
        queryClient.setQueryData(meetingFavoritesKey(user?.id), context.previousFavorites);
      }
      toast({
        title: 'Lỗi',
        description: 'Không thể cập nhật trạng thái yêu thích',
        variant: 'destructive',
      });
    },
    onSuccess: (_, { isFavorite }) => {
      toast({
        title: isFavorite ? 'Đã bỏ yêu thích' : 'Đã thêm vào yêu thích',
        description: isFavorite 
          ? 'Cuộc họp đã được bỏ khỏi danh sách yêu thích' 
          : 'Cuộc họp đã được thêm vào danh sách yêu thích',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: meetingFavoritesKey(user?.id) });
    },
  });
};

/**
 * Helper hook to check if a specific meeting is favorited
 */
export const useIsMeetingFavorited = (meetingId: string) => {
  const { data: favoriteIds, isLoading } = useFavoriteMeetingIds();
  
  return {
    isFavorite: favoriteIds?.has(meetingId) ?? false,
    isLoading,
  };
};
